import {IUUserContext} from '../contexts/UserContext';
import {createUnauthHeader} from '../lib/performance';
import logRpc from '../util/logRpc';

import Net from 'Common/src/lib/Net';
import {augmentService} from 'Common/src/lib/ThriftServiceAugmentor';

import {TElkEventService} from 'PProtocol/prototypes/events';
import {
  TAppElkAttendeeStatus,
  TAppElkEvent,
  TAppElkInvitee,
  TAppElkMessage,
  TAppElkTextPermissionStatus,
  TAppElkUser,
  TElkCancelEventRequest,
  TElkCancelEventResponse,
  TElkCreateEventRequest,
  TElkCreateEventResponse,
  TElkCreateUserRequest,
  TElkCreateUserResponse,
  TElkDeleteEventRequest,
  TElkDeletePendingInviteRequest,
  TElkEditEventDetailsRequest,
  TElkEditEventDetailsResponse,
  TElkEditInviteeRequest,
  TElkFetchAlbumRequest,
  TElkFetchEventsRequest,
  TElkFetchEventsResponse,
  TElkGetAlbumDetailsRequest,
  TElkGetEventPreviewRequest,
  TElkGetEventRequest,
  TElkGetEventShortLinkRequest,
  TElkGetEventShortLinkResponse,
  TElkGetHasCompletedProfileRequest,
  TElkGetHasCompletedProfileResponse,
  TElkGetImageHistoryRequest,
  TElkGetMessageHistoryRequest,
  TElkGetUserRequest,
  TElkGetUserResponse,
  TElkQueueInviteRequest,
  TElkSendCommentRequest,
  TElkSendCommentResponse,
  TElkSendInviteRequest,
  TElkSendTemplateEmailRequest,
  TElkSendTemplateEmailResponse,
  TElkUpdateAttendanceRequest,
  TElkUpdateAttendanceResponse,
  TElkUpdateUserRequest,
  TElkUpdateUserResponse,
} from 'TProtocol/prototypes/events/messages';
import {TAuthHeader} from 'TProtocol/common/models';

const createService = (): TElkEventService.Client => {
  return augmentService(Net.createService(TElkEventService, '/json/ElkEventService'));
};

export const createElkUser = async (
  header: TAuthHeader,
  user: TAppElkUser,
  coinsSpent?: number
  ): Promise<TElkCreateUserResponse> => {
  logRpc(null, 'createElkUser');
  const request: TElkCreateUserRequest = new TElkCreateUserRequest({
    elkUser: user,
    coinsSpent
  });
  const client: TElkEventService.Client = createService();
  return client.createElkUser(header, request);
};

export const getElkUser = async (header: TAuthHeader): Promise<TElkGetUserResponse> => {
  logRpc(null, 'getElkUser');
  const request: TElkGetUserRequest = new TElkGetUserRequest({ userId: header.userId });
  const client: TElkEventService.Client = createService();
  return client.getElkUser(header, request);
};

export const updateElkUser = async (header: TAuthHeader, elkUser: TAppElkUser): Promise<TElkUpdateUserResponse> => {
  logRpc(null, 'updateElkUser');
  const request: TElkUpdateUserRequest = new TElkUpdateUserRequest({ elkUser });
  const client: TElkEventService.Client = createService();
  return client.updateElkUser(header, request);
};

export const createEvent = async (userContext: IUUserContext,
  event: TAppElkEvent, isBlocking: boolean, albumId?: string): Promise<TElkCreateEventResponse> => {
  logRpc(userContext, 'createEvent');
  const request: TElkCreateEventRequest = new TElkCreateEventRequest({
    event: event,
    albumId
  });
  const client: TElkEventService.Client = createService();
  return client.createEvent(userContext.getAuthHeader(isBlocking), request);
};

export const editEventDetails = async (userContext: IUUserContext,
  event: TAppElkEvent, disableNotification: boolean, isBlocking: boolean): Promise<TElkEditEventDetailsResponse> => {
  logRpc(userContext, 'editEventDetails');
  const request: TElkEditEventDetailsRequest = new TElkEditEventDetailsRequest(
    {
      event,
      eventId: event.id,
      disableNotification
    }
  );
  const client: TElkEventService.Client = createService();
  return client.editEventDetails(userContext.getAuthHeader(isBlocking), request);
};

export const fetchEvents = async (userContext: IUUserContext,
  isBlocking: boolean): Promise<TElkFetchEventsResponse> => {
  logRpc(userContext, 'fetchEvents');
  const request: TElkFetchEventsRequest = new TElkFetchEventsRequest();
  const client: TElkEventService.Client = createService();
  return client.fetchEvents(userContext.getAuthHeader(isBlocking), request);
};

export const getEvent = async (userContext: IUUserContext, eventId: string, inviteeUuid: string | undefined,
  isBlocking: boolean): Promise<TAppElkEvent> => {
  logRpc(userContext, 'getEvent');
  const request: TElkGetEventRequest = new TElkGetEventRequest({
    eventId,
    inviteeUuid
  });
  const client: TElkEventService.Client = createService();

  return (await client.getEvent(userContext.getAuthHeader(isBlocking), request)).event;
};

export const getEventPreview = async (eventId: string, inviteeUuid: string | undefined, isBlocking: boolean): Promise<TAppElkEvent> => {
  logRpc(null, 'getEventPreview');
  const request: TElkGetEventPreviewRequest = new TElkGetEventPreviewRequest({
    eventId,
    inviteeUuid
  });
  const client: TElkEventService.Client = createService();

  return (await client.getEventPreview(createUnauthHeader(isBlocking), request)).event;
};

export const updateAttendance = async (userContext: IUUserContext, eventId: string, invitee: TAppElkInvitee,
  textPermissionStatusUpdate: TAppElkTextPermissionStatus | undefined, token: string | undefined,
  isBlocking: boolean): Promise<TElkUpdateAttendanceResponse> => {
  logRpc(userContext, 'updateAttendance');
  const request: TElkUpdateAttendanceRequest = new TElkUpdateAttendanceRequest(
    { eventId, invitee, textPermissionStatusUpdate, token });
  const client: TElkEventService.Client = createService();
  return client.updateAttendance(userContext.getAuthHeader(isBlocking), request);
};

export const updateAttendanceThroughEmail = async (eventId: string, invitee: TAppElkInvitee,
                                       textPermissionStatusUpdate: TAppElkTextPermissionStatus | undefined, token: string,
                                       isBlocking: boolean): Promise<TElkUpdateAttendanceResponse> => {
  logRpc(null, 'updateAttendanceThroughEmail');
  const request: TElkUpdateAttendanceRequest = new TElkUpdateAttendanceRequest(
      { eventId, invitee, textPermissionStatusUpdate, token });
  const client: TElkEventService.Client = createService();
  return client.updateAttendanceThroughEmail(createUnauthHeader(isBlocking), request);
};

export const getHasCompletedElkProfile = async (phoneNumber: string,
  isBlocking: boolean): Promise<TElkGetHasCompletedProfileResponse> => {
  logRpc(null, 'getHasCompletedElkProfile');
  const request: TElkGetHasCompletedProfileRequest = new TElkGetHasCompletedProfileRequest({
    phoneNumber
  });
  const client: TElkEventService.Client = createService();

  return client.getHasCompletedElkProfile(createUnauthHeader(isBlocking), request);
};

export const getEventShortLink = async (userContext: IUUserContext,
  eventId: string): Promise<TElkGetEventShortLinkResponse> => {
  logRpc(userContext, 'getEventShortLink');
  const request: TElkGetEventShortLinkRequest = new TElkGetEventShortLinkRequest({
    eventId
  });
  const client: TElkEventService.Client = createService();

  return client.getEventShortLink(userContext.getAuthHeader(false), request);
};

export const cancelEvent = async (userContext: IUUserContext, eventId: string): Promise<TElkCancelEventResponse> => {
  logRpc(userContext, 'cancelEvent');
  const request: TElkCancelEventRequest = new TElkCancelEventRequest({
    eventId
  });
  const client: TElkEventService.Client = createService();

  return client.cancelEvent(userContext.getAuthHeader(false), request);
};

export const sendEventComment = async ({ userContext, eventId, messageId, message, sendToStatuses, email, inviteeId }: {
  userContext: IUUserContext,
  eventId: string,
  messageId: string,
  message: string,
  sendToStatuses?: TAppElkAttendeeStatus[],
  email?: string,
  inviteeId?: string
}): Promise<TElkSendCommentResponse> => {
  logRpc(userContext, 'sendEventComment');
  const request: TElkSendCommentRequest = new TElkSendCommentRequest({
    eventId,
    messageId,
    message,
    isTextBlast: inviteeId === undefined,
    sendToStatuses,
    email,
    receiverInviteeUuid: inviteeId
  });

  const client: TElkEventService.Client = createService();
  return client.sendEventComment(userContext.getAuthHeader(false), request);
};

export const getEventMessageHistory = async (userContext: IUUserContext,
  eventId: string, inviteeUuid?: string): Promise<TAppElkMessage[]> => {
  logRpc(userContext, 'getEventMessageHistory');
  const request: TElkGetMessageHistoryRequest = new TElkGetMessageHistoryRequest({
    eventId,
    inviteeUuid
  });

  const client: TElkEventService.Client = createService();
  return (await client.getEventMessageHistory(createUnauthHeader(false), request)).messages;
};

export const getUserEventMessageHistory = async (userContext: IUUserContext,
  eventId: string, inviteeUuid?: string): Promise<TAppElkMessage[]> => {
  logRpc(userContext, 'getUserEventMessageHistory');
  const request: TElkGetMessageHistoryRequest = new TElkGetMessageHistoryRequest({
    eventId,
    inviteeUuid
  });

  const client: TElkEventService.Client = createService();
  return (await client.getUserEventMessageHistory(userContext.getAuthHeader(), request)).messages;
};

export const sendTemplateEmail = async (userContext: IUUserContext, eventId: string,
  emailAddress: string): Promise<TElkSendTemplateEmailResponse> => {
  logRpc(userContext, 'sendTemplateEmail');
  const request: TElkSendTemplateEmailRequest = new TElkSendTemplateEmailRequest({
    eventId,
    emailAddress
  });

  const client: TElkEventService.Client = createService();
  return client.sendTemplateEmail(userContext.getAuthHeader(), request);
};
export const deleteEvent = async (userContext: IUUserContext, eventId: string) => {
  logRpc(userContext, 'deleteEvent');
  const request: TElkDeleteEventRequest = new TElkDeleteEventRequest({
    eventId
  });
  const client: TElkEventService.Client = createService();

  return client.deleteEvent(userContext.getAuthHeader(false), request);
};

export const queueInvites = async (userContext: IUUserContext, eventId: string, invitees: TAppElkInvitee[]) => {
  logRpc(userContext, 'queueInvites');
  const request: TElkQueueInviteRequest = new TElkQueueInviteRequest({
    eventId,
    invitees
  });
  const client: TElkEventService.Client = createService();

  return client.queueInvites(userContext.getAuthHeader(false), request);
}

export const sendInvites = async (userContext: IUUserContext, eventId: string, inviteIds: string[], email?: string) => {
  logRpc(userContext, 'sendInvites');
  const request: TElkSendInviteRequest = new TElkSendInviteRequest({
    eventId,
    inviteIds,
    email
  });
  const client: TElkEventService.Client = createService();

  return client.sendInvites(userContext.getAuthHeader(false), request);
}

export const deletePendingInvites = async (userContext: IUUserContext, eventId: string, inviteIds: string[]) => {
  logRpc(userContext, 'deletePendingInvites');
  const request: TElkDeletePendingInviteRequest = new TElkDeletePendingInviteRequest({
    eventId,
    inviteIds
  });
  const client: TElkEventService.Client = createService();

  return client.deletePendingInvites(userContext.getAuthHeader(false), request);
}

export const getEventImageHistory = async (userContext: IUUserContext, eventId?: string) => {
  logRpc(userContext, 'getEventImageHistory');
  const request = new TElkGetImageHistoryRequest({
    eventId
  });

  const client: TElkEventService.Client = createService();

  return client.getEventImageHistory(userContext.getAuthHeader(false), request);
}

export const getAlbumDetails = async (userContext: IUUserContext, eventId: string) => {
  logRpc(userContext, 'getAlbumDetails');
  const request = new TElkGetAlbumDetailsRequest({
    eventId
  });

  const client: TElkEventService.Client = createService();

  return client.getAlbumDetails(userContext.getAuthHeader(false), request);
}

export const getAlbumForElk = async (userContext: IUUserContext, albumId: string) => {
  logRpc(userContext, 'getAlbumForElk');
  const request = new TElkFetchAlbumRequest({
    albumId
  });

  const client: TElkEventService.Client = createService();

  return client.getAlbumForElk(userContext.getAuthHeader(false), request);
}

export const getPreviewAlbumDetails = async (eventId: string, token?: string) => {
  logRpc(null, 'getPreviewAlbumDetails');
  const request = new TElkGetAlbumDetailsRequest({
    eventId,
    token
  });

  const client: TElkEventService.Client = createService();

  return client.getPreviewAlbumDetails(createUnauthHeader(false), request);
}

export const editInvitee = async (userContext: IUUserContext, eventId: string, invitee: TAppElkInvitee) => {
  logRpc(null, 'editInvitee');
  const request = new TElkEditInviteeRequest({
    eventId,
    invitee
  });

  const client: TElkEventService.Client = createService();

  return client.editInvitee(userContext.getAuthHeader(false), request);
};
